import { __awaiter, __rest } from "tslib";
import { UTCDateTimeStringToDateString } from '../_internal/conversion/converters/UTCDateTimeStringToDate';
import { UTCUnixToDate } from '../_internal/conversion/converters/UTCUnixToDate';
import { convert } from '../_internal/conversion/module';
import { CompanionError, isCompanionError } from '../_internal/error/CompanionError';
import { isHttpError } from '../_internal/error/HttpError';
import * as api from '../api/module';
import { getConfigValueOrThrow } from '../config';
import { getCurrentSiteId, getInitialCurrentSiteId, setCurrentSiteId } from '../sites/cache';
import { deleteCookie, getCookie, setCookie } from '../utils/cookies';
import { emitEvent } from '../utils/events/module';
import { Logger } from '../utils/logging/module';
import { clearCurrentSession, getCachedSession, getCurrentSessionToken, setCurrentSession, setCurrentSessionToken, } from './cache';
import { setTimers } from './timers';
const L = new Logger('SESSION');
const sessionConversionConfig = {
    created_date: UTCUnixToDate,
    subscription_expiration: UTCUnixToDate,
    'user.verified_date': UTCDateTimeStringToDateString,
};
/**
 * Create a new session with the provided credentials
 */
export function createSession({ email, password, mfaCode, rememberMFA, }) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const data = {
                // Email and password are always required
                email,
                password,
            };
            if (mfaCode) {
                // Login with MFA code
                data.code = mfaCode;
                data.remember_mfa = !!rememberMFA;
            }
            else {
                // Login with cached MFA signature
                const mfaSignature = getCookie(getConfigValueOrThrow('AUTH_MFA_COOKIE_IDENTIFIER'));
                if (mfaSignature) {
                    data.mfa_signature = mfaSignature;
                }
            }
            const res = yield api.post('/v2/dashboard_scope/', {
                data,
                requiresAuth: false,
            });
            // Empty content, MFA required
            if (res.status === 204) {
                // Remove existing MFA cookie since it's no longer valid
                deleteCookie(getConfigValueOrThrow('AUTH_MFA_COOKIE_IDENTIFIER'));
                throw new CompanionError({
                    res,
                    code: 'MFA_REQUIRED',
                });
            }
            const session = convert('toClient', res.data, sessionConversionConfig);
            // Delete any lingering admin cookies when creating a new session because
            // sessions created from the admin dashboard will never go through this login function
            deleteCookie(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'));
            // Set cookie to remember MFA if endpoint returns MFA code
            if (session.authentication.mfa && typeof session.authentication.mfa === 'string') {
                setCookie(getConfigValueOrThrow('AUTH_MFA_COOKIE_IDENTIFIER'), session.authentication.mfa, {
                    // 14 days
                    'Max-Age': 60 * 60 * 24 * 14,
                });
            }
            setCurrentSession(session);
            setCurrentSiteId(getInitialCurrentSiteId(session));
            setTimers();
            emitEvent('session', 'created', session);
            L.log('Session created!');
            return session;
        }
        catch (e) {
            if (isHttpError(e)) {
                // Invalid login
                if (e.httpStatus === 400 || e.httpStatus === 403) {
                    throw new CompanionError({
                        error: e,
                        code: 'CREDENTIALS_INVALID',
                    });
                }
                // Password has been revoked
                if (isHttpError(e) && e.httpStatus === 409) {
                    throw new CompanionError({
                        error: e,
                        code: 'PASSWORD_REVOKED',
                    });
                }
            }
            // If already a companion error, throw as-is
            if (isCompanionError(e)) {
                throw e;
            }
            // Convert any other unknown error to a companion error
            throw new CompanionError({
                error: e,
                code: 'UNKNOWN',
            });
        }
    });
}
createSession.ERR = {
    UNKNOWN: 'UNKNOWN',
    CREDENTIALS_INVALID: 'CREDENTIALS_INVALID',
    MFA_REQUIRED: 'MFA_REQUIRED',
    PASSWORD_REVOKED: 'PASSWORD_REVOKED',
};
/**
 * End a session (delete the auth cookie) and optionally invalidate the server session too.
 *
 * @param invalidateOnServer If the session should be invalidated server side
 */
export function endSession(invalidateOnServer = true) {
    return __awaiter(this, void 0, void 0, function* () {
        L.log('Ending session...');
        // Prevent JW users logged in through admin from destroying customer sessions
        if (getAuthFromAdminMetadata()) {
            invalidateOnServer = false;
        }
        const promise = invalidateOnServer ? api.remove('/v2/dashboard_scope/', { requiresAuth: true }) : Promise.resolve();
        // Clear all local state and cookies
        clearCurrentSession();
        emitEvent('session', 'ended');
        L.log('Session ended!');
        yield promise;
    });
}
/**
 * Retrieve the current session object from the cache, or refresh the session and then return it.
 */
export function getCurrentSession(forceRefresh = false) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (!forceRefresh) {
            const cachedSession = getCachedSession();
            if (cachedSession) {
                // Return cached session if it's still valid
                return cachedSession;
            }
        }
        // First check for the an "auth from admin" query param.
        // In this case we're creating a session from the admin dashboard.
        const usp = new URLSearchParams(window.location.search);
        if (usp.has(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'))) {
            try {
                // If the string is incorrect then JSON.parse will throw
                const payload = JSON.parse(atob((_a = usp.get(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'))) !== null && _a !== void 0 ? _a : ''));
                if (!(payload &&
                    typeof payload === 'object' &&
                    'authToken' in payload &&
                    typeof payload.authToken === 'string' &&
                    payload.authToken)) {
                    throw new Error('Missing authToken');
                }
                if (!verifyAuthFromAdminMetadata(payload)) {
                    throw new Error('Invalid payload');
                }
                const { authToken } = payload, meta = __rest(payload, ["authToken"]);
                // Set token given by admin dashboard
                // We'll use this to refresh the session
                setCurrentSessionToken(authToken);
                // Set metadata for admin-login in cookie
                setCookie(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'), JSON.stringify(meta));
            }
            catch (err) {
                console.warn('Login through admin dashboard failed. Invalid payload.');
                // Set a cookie that lets us know that login through admin failed
                setCookie(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'), JSON.stringify({ error: true }));
            }
        }
        L.log('Attempting to refresh session...');
        const session = yield refreshSession();
        return session;
    });
}
/**
 * Refresh the currently active session.
 *
 * This method calls the dashboard_scope with a GET request using the current authorization token.
 * The endpoint then provides a new, refreshed token with reset expiration.
 */
export function refreshSession() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!getCurrentSessionToken()) {
            // We can't refresh the session if we don't even have a stored token
            throw new CompanionError({
                error: new Error('Missing auth token'),
                code: 'AUTH_TOKEN_MISSING',
            });
        }
        try {
            const res = yield api.get('/v2/dashboard_scope/', { requiresAuth: true });
            const session = convert('toClient', res.data, sessionConversionConfig);
            setCurrentSession(session);
            setTimers();
            // Check if there's a current site id, and if it exists in the session object
            const currentSiteId = getCurrentSiteId();
            if (typeof currentSiteId === 'undefined' || !session.sites.find((site) => site.site_id === currentSiteId)) {
                // Revert to a valid current site
                setCurrentSiteId(getInitialCurrentSiteId(session));
            }
            L.log('Session refreshed!');
            emitEvent('session', 'refreshed', session);
            return session;
        }
        catch (err) {
            // Remove cookies as soon as they were not able to create a session
            if (isHttpError(err) && `${err.httpStatus}`.startsWith('4')) {
                clearCurrentSession();
            }
            // Re-throw!
            throw err;
        }
    });
}
/**
 * Check if current session was established in the admin dashboard and get metadata.
 */
export function getAuthFromAdminMetadata() {
    const cookieValue = getCookie(getConfigValueOrThrow('AUTH_FROM_ADMIN_KEY'));
    if (!cookieValue) {
        return;
    }
    try {
        const meta = JSON.parse(cookieValue);
        if (verifyAuthFromAdminMetadata(meta)) {
            return meta;
        }
    }
    catch (err) {
        // ignore
    }
}
refreshSession.ERR = {
    AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
};
/**
 * Verify that the auth from admin payload has the correct properties
 */
function verifyAuthFromAdminMetadata(data) {
    return Boolean(data &&
        typeof data === 'object' &&
        'readOnly' in data &&
        typeof data.readOnly === 'boolean' &&
        'username' in data &&
        typeof data.username === 'string' &&
        data.username &&
        'date' in data &&
        typeof data.date === 'string' &&
        data.date);
}
